import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface CreateTenantRequest {
  name: string;
  slug: string;
  domain?: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  settings?: {
    timezone?: string;
    language?: string;
    currency?: string;
  };
}

export interface CreateAdminUserRequest {
  email: string;
  name: string;
  password: string;
}

export interface TenantResponse {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  status: 'active' | 'inactive' | 'suspended';
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
  settings?: {
    timezone?: string;
    language?: string;
    currency?: string;
  };
}

export interface SlugValidationResponse {
  available: boolean;
  suggestion?: string;
}

class TenantService {
  // The backend POST /tenants creates the tenant AND its admin user in ONE
  // transaction (and emits tenant.created -> employee provisioning). It expects a
  // FLAT DTO: `subdomain` (not `slug`), inline `admin*`, optional timezone/currency.
  // subscriptionPlan is a strict backend enum and the wizard's tier label won't
  // match it, so we omit it (defaults to free) and set the plan/entitlements
  // separately after creation.
  async createTenant(
    tenant: CreateTenantRequest,
    admin: CreateAdminUserRequest,
  ): Promise<TenantResponse> {
    try {
      const body = {
        name: tenant.name,
        subdomain: tenant.slug,
        domain: tenant.domain || undefined,
        adminEmail: admin.email,
        adminName: admin.name,
        adminPassword: admin.password,
        timezone: tenant.settings?.timezone,
        currency: tenant.settings?.currency,
      };
      const response = await axios.post<TenantResponse>(`${API_URL}/tenants`, body);
      return response.data;
    } catch (error) {
      console.error('Failed to create tenant:', error);
      throw error;
    }
  }

  async validateSlug(slug: string): Promise<SlugValidationResponse> {
    try {
      const response = await axios.get<SlugValidationResponse>(
        `${API_URL}/tenants/validate-slug/${encodeURIComponent(slug)}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to validate slug:', error);
      throw error;
    }
  }

  async createAdminUser(tenantId: string, data: CreateAdminUserRequest): Promise<void> {
    try {
      await axios.post(`${API_URL}/tenants/${tenantId}/admin-user`, data);
    } catch (error) {
      console.error('Failed to create admin user:', error);
      throw error;
    }
  }

}

export const tenantService = new TenantService();

// --- Platform operator: live tenant management (auth-service) ---
// Normalize the base to /api/v1 regardless of how VITE_API_URL is configured.
const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  domain?: string | null;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
  isActive?: boolean;
  createdAt?: string;
  _count?: { users?: number };
  [k: string]: unknown;
}
export interface TenantsPageResult {
  tenants: AdminTenant[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const listTenants = (params?: {
  page?: number; limit?: number; search?: string;
  status?: 'active' | 'suspended' | 'terminated'; plan?: string;
}) => axios.get<TenantsPageResult>(`${API_V1}/tenants`, { params }).then((r) => r.data);

export const getTenantById = (id: string) =>
  axios.get<AdminTenant>(`${API_V1}/tenants/${id}`).then((r) => r.data);

// Suspend/reactivate/terminate carry an operator reason → immutable audit
// (auth-service writes it audit-first, fail-closed).
export const deactivateTenant = (id: string, reason?: string) =>
  axios.delete(`${API_V1}/tenants/${id}/deactivate`, { data: { reason } }).then((r) => r.data);

export const activateTenant = (id: string, reason?: string) =>
  axios.put(`${API_V1}/tenants/${id}/activate`, { reason }).then((r) => r.data);

/** Terminate = soft-delete (sets deletedAt; the gateway then treats the tenant as
 * 'deleted' and blocks). Destructive — the UI requires a type-to-confirm phrase. */
export const terminateTenant = (id: string, reason: string) =>
  axios.post(`${API_V1}/tenants/${id}/terminate`, { reason }).then((r) => r.data);

// Operator impersonation: mint a session AS the tenant's admin (resolved
// server-side) for support. Audited in auth-service. Returns handoff tokens.
export interface ImpersonationSession {
  accessToken: string;
  refreshToken: string;
  tenantSlug?: string;
  user: { id: string; email: string; tenantId: string };
}
export const impersonateTenantAdmin = (tenantId: string) =>
  axios
    .post<ImpersonationSession>(`${API_V1}/users/impersonate-tenant/${tenantId}`)
    .then((r) => r.data);

// Operator-set entitlements: plan + seat limit.
export const updateTenantEntitlements = (
  id: string,
  data: { subscriptionPlan?: string; maxUsers?: number },
) => axios.put<AdminTenant>(`${API_V1}/tenants/${id}`, data).then((r) => r.data);

// --- Gated company registration: application review (auth-service) ---

export type ApplicationStatus =
  | 'submitted'
  | 'approved'
  | 'completed'
  | 'rejected'
  | 'expired';

export interface TenantApplication {
  id: string;
  companyName: string;
  email: string;
  tin: string;
  status: ApplicationStatus;
  rejectionReason?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  tenantId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export const listApplications = (status?: string) =>
  axios
    .get<TenantApplication[]>(`${API_V1}/tenant-applications/admin`, {
      params: status ? { status } : undefined,
    })
    .then((r) => r.data);

export const approveApplication = (id: string) =>
  axios
    .post<{ status: string; id: string }>(`${API_V1}/tenant-applications/admin/${id}/approve`)
    .then((r) => r.data);

export const rejectApplication = (id: string, reason?: string) =>
  axios
    .post<{ status: string; id: string }>(`${API_V1}/tenant-applications/admin/${id}/reject`, {
      reason,
    })
    .then((r) => r.data);

export interface RevealedCode {
  email: string;
  companyName: string;
  code: string;
  verifyUrl: string;
  expiresAt: string;
}

// Email-delivery fallback: regenerate the verification code and return it so an
// operator can relay it directly (e.g. when a corporate mail server drops it).
export const revealApplicationCode = (id: string) =>
  axios
    .post<RevealedCode>(`${API_V1}/tenant-applications/admin/${id}/reveal-code`)
    .then((r) => r.data);

// Per-tenant feature entitlements.
export interface TenantFeatureRow {
  featureCode: string;
  isEnabled: boolean;
}
export const getTenantFeatures = (id: string) =>
  axios
    .get<{ features: TenantFeatureRow[] }>(`${API_V1}/tenants/${id}/features`)
    .then((r) => r.data.features);
export const setTenantFeature = (id: string, featureCode: string, isEnabled: boolean) =>
  axios.put(`${API_V1}/tenants/${id}/features`, { featureCode, isEnabled }).then((r) => r.data);

// --- Product Catalogue + Tenant Product Entitlements (E0/E1/E2) ---
export interface CatalogProduct {
  id: string;
  code: string;
  name: string;
  productType: string;
  ownerType: string;
  status: string;
  metadata?: Record<string, unknown>;
  dependencies?: { code: string; name: string; kind: string }[];
}
export const listCatalog = () =>
  axios.get<{ products: CatalogProduct[] }>(`${API_V1}/admin/products`).then((r) => r.data.products);

export interface Entitlement {
  id: string;
  productCode: string;
  productName: string;
  productType?: string | null;
  status: string;
  source: string;
  planId?: string | null;
  provisioning?: string;
  limits?: Record<string, unknown>;
  validFrom?: string | null;
  validTo?: string | null;
  suspensionReason?: string | null;
  version: number;
  isEnabled?: boolean | null;
  configuration?: Record<string, unknown>;
  updatedAt?: string | null;
}
export const listEntitlements = (tenantId: string) =>
  axios.get<{ entitlements: Entitlement[] }>(`${API_V1}/tenants/${tenantId}/entitlements`).then((r) => r.data.entitlements);
export const grantEntitlement = (
  tenantId: string,
  body: { productCode: string; status?: string; source?: string; planId?: string; configuration?: Record<string, unknown> },
) => axios.post(`${API_V1}/tenants/${tenantId}/entitlements`, body).then((r) => r.data);
export const transitionEntitlement = (tenantId: string, id: string, action: string, body?: { reason?: string }) =>
  axios.post(`${API_V1}/tenants/${tenantId}/entitlements/${id}/${action}`, body ?? {}).then((r) => r.data);
export interface EntitlementHistoryRow {
  id: string;
  action: string;
  userId?: string | null;
  previousState?: any;
  newState?: any;
  metadata?: any;
  timestamp?: string;
}
export const entitlementHistory = (tenantId: string, id: string) =>
  axios.get<{ history: EntitlementHistoryRow[] }>(`${API_V1}/tenants/${tenantId}/entitlements/${id}/history`).then((r) => r.data.history);

// --- Commercial packaging (Phases 1/3): trials, overrides, explain ---
export interface EntitlementOverride {
  id: string;
  productCode: string;
  kind: 'grant' | 'deny' | 'limit_override';
  limits?: Record<string, unknown>;
  configuration?: Record<string, unknown>;
  effectiveFrom: string;
  effectiveTo?: string | null;
  reason: string;
  contractRef?: string | null;
  createdAt: string;
  revokedAt?: string | null;
  revokeReason?: string | null;
}
export const startTrial = (tenantId: string, body: { days: number; productCodes?: string[] }) =>
  axios.post(`${API_V1}/tenants/${tenantId}/trial`, body).then((r) => r.data);
export const extendTrial = (tenantId: string, body: { days: number; reason: string }) =>
  axios.post(`${API_V1}/tenants/${tenantId}/trial/extend`, body).then((r) => r.data);
export const listOverrides = (tenantId: string) =>
  axios.get<{ overrides: EntitlementOverride[] }>(`${API_V1}/tenants/${tenantId}/entitlement-overrides`).then((r) => r.data.overrides);
export const createOverride = (
  tenantId: string,
  body: { productCode: string; kind?: string; limits?: Record<string, unknown>; effectiveTo?: string; reason: string; contractRef?: string },
) => axios.post<EntitlementOverride>(`${API_V1}/tenants/${tenantId}/entitlement-overrides`, body).then((r) => r.data);
export const revokeOverride = (tenantId: string, overrideId: string, reason: string) =>
  axios.post(`${API_V1}/tenants/${tenantId}/entitlement-overrides/${overrideId}/revoke`, { reason }).then((r) => r.data);
export const explainEntitlement = (tenantId: string, code: string) =>
  axios.get(`${API_V1}/tenants/${tenantId}/entitlements/explain/${code}`).then((r) => r.data);
